import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";

// ---- small building blocks ----
function Section({ title, hint, children }) {
  return (
    <section className="border border-foreground p-6 md:p-8">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted-foreground max-w-xl">{hint}</p>}
      <div className="mt-5 space-y-4">{children}</div>
    </section>
  );
}
function Field({ label, value, onChange, placeholder, locked, hint, half }) {
  return (
    <div className={half ? "" : "w-full"}>
      <label className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}{locked && " · locked"}</label>
      {locked
        ? <p className="mt-1 text-sm border-b border-foreground/15 pb-2">{value || "—"}</p>
        : <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="underline-input w-full" />}
      {hint && <p className="mt-1 font-mono text-[10px] text-muted-foreground">{hint}</p>}
    </div>
  );
}
function SaveBtn({ onClick, busy, children = "Save" }) {
  return (
    <button disabled={busy} onClick={onClick}
      className="bg-foreground text-background px-6 py-2.5 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
      {busy ? "Saving…" : children}
    </button>
  );
}
function DocRow({ label, uploaded, onUpload, hint }) {
  const ref = useRef(null);
  const pick = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 3_000_000) return toast.error("Please use a file under 3 MB.");
    const r = new FileReader();
    r.onload = () => onUpload(r.result);
    r.readAsDataURL(f);
  };
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-foreground/12">
      <div>
        <p className="text-sm">{label}</p>
        {hint && <p className="font-mono text-[10px] text-muted-foreground">{hint}</p>}
      </div>
      <div className="flex items-center gap-3">
        {uploaded && <span className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">✓ On file</span>}
        <button onClick={() => ref.current?.click()} className="border border-foreground px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background">{uploaded ? "Replace" : "Upload"}</button>
        <input ref={ref} type="file" accept="application/pdf,image/*" onChange={pick} className="hidden" />
      </div>
    </div>
  );
}

const A = (a) => a || { address: "", city: "", state: "", pincode: "" };

export default function VendorProfile() {
  const { reloadVendor } = useOutletContext() || {};
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState("");
  const sigRef = useRef(null);

  const load = async () => { try { setP((await api.get("/vendor/profile")).data); } catch { toast.error("Couldn't load profile"); } };
  useEffect(() => { load(); }, []);
  if (!p) return <div className="font-mono text-sm">Loading…</div>;

  const set = (path, val) => setP((prev) => {
    const next = structuredClone(prev);
    let o = next; const keys = path.split("."); const last = keys.pop();
    for (const k of keys) o = o[k];
    o[last] = val; return next;
  });

  const saveBusiness = async () => {
    setBusy("business");
    try {
      await api.patch("/vendor/profile/business", {
        store_name: p.store.store_name, contact_person: p.business.contact_person,
        designation: p.business.designation, mobile: p.business.mobile,
      });
      toast.success("Business details saved"); reloadVendor && reloadVendor();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const saveAddresses = async () => {
    setBusy("addr");
    try {
      await api.patch("/vendor/profile/addresses", {
        reg_office: p.addresses.reg_office, pickup_same: p.addresses.pickup_same, pickup: p.addresses.pickup,
      });
      toast.success("Addresses saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const saveBank = async () => {
    setBusy("bank");
    try {
      await api.patch("/vendor/profile/bank", {
        account_holder: p.bank.account_holder, bank_name: p.bank.bank_name, branch: p.bank.branch,
        account_number: p.bank._new_account || "", ifsc: p.bank.ifsc, account_type: p.bank.account_type,
      });
      toast.success("Bank details saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const uploadDoc = async (kind, image) => {
    try { await api.post("/vendor/profile/document", { kind, image }); toast.success("Document uploaded"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't upload"); }
  };
  const saveInvoicing = async () => {
    setBusy("inv");
    try {
      await api.put("/vendor/invoicing", {
        mode: p.invoicing.mode, prefix: p.invoicing.prefix,
        next_seq: Number(p.invoicing.next_seq) || 1, pad: Number(p.invoicing.pad) || 0,
      });
      toast.success("Invoicing saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(""); }
  };
  const uploadSig = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.size > 1_500_000) return toast.error("Please use an image under 1.5 MB.");
    const r = new FileReader();
    r.onload = async () => {
      try { await api.post("/vendor/signature", { image: r.result }); toast.success("Signature saved"); set("has_signature", true); }
      catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    };
    r.readAsDataURL(f);
  };

  const invPreview = `${p.invoicing.prefix || ""}${String(p.invoicing.next_seq || 1).padStart(Number(p.invoicing.pad) || 0, "0")}`;
  const ro = A(p.addresses.reg_office), pk = A(p.addresses.pickup);

  return (
    <div className="max-w-[760px]">
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Vendor</p>
      <h1 className="font-serif text-5xl tracking-tighter">Profile.</h1>
      <p className="mt-2 font-mono text-[11px] text-muted-foreground">
        {p.store.store_name}{p.store.vendor_code ? ` · ${p.store.vendor_code}` : ""} · Status {p.store.status?.toUpperCase()}
      </p>

      <div className="mt-8 space-y-6">
        {/* 1. Business & contact */}
        <Section title="Business & contact">
          <Field label="Display / store name" value={p.store.store_name} onChange={(v) => set("store.store_name", v)} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field half label="Legal name" value={p.business.legal_name} locked />
            <Field half label="Trade name" value={p.business.trade_name} locked />
            <Field half label="Constitution" value={p.business.constitution} locked />
            <Field half label="Account email" value={p.business.email} locked hint="Your login email." />
            <Field half label="Contact person" value={p.business.contact_person} onChange={(v) => set("business.contact_person", v)} />
            <Field half label="Designation" value={p.business.designation} onChange={(v) => set("business.designation", v)} />
            <Field half label="Mobile" value={p.business.mobile} onChange={(v) => set("business.mobile", v)} />
          </div>
          <SaveBtn onClick={saveBusiness} busy={busy === "business"} />
        </Section>

        {/* 2. Addresses */}
        <Section title="Addresses" hint="Your registered office must match your GST certificate. Pickup is where couriers collect parcels.">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Registered office (as per GST)</p>
          <Field label="Address" value={ro.address} onChange={(v) => set("addresses.reg_office.address", v)} />
          <div className="grid sm:grid-cols-3 gap-4">
            <Field half label="City" value={ro.city} onChange={(v) => set("addresses.reg_office.city", v)} />
            <Field half label="State" value={ro.state} onChange={(v) => set("addresses.reg_office.state", v)} />
            <Field half label="PIN code" value={ro.pincode} onChange={(v) => set("addresses.reg_office.pincode", v)} />
          </div>
          <label className="flex items-center gap-2.5 pt-2 font-mono text-[11px] cursor-pointer select-none">
            <input type="checkbox" checked={!!p.addresses.pickup_same} onChange={(e) => set("addresses.pickup_same", e.target.checked)} className="w-4 h-4 accent-[hsl(var(--accent))]" />
            Pickup address is the same as my registered office
          </label>
          {!p.addresses.pickup_same && (
            <>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground pt-2">Pickup address</p>
              <Field label="Address" value={pk.address} onChange={(v) => set("addresses.pickup.address", v)} />
              <div className="grid sm:grid-cols-3 gap-4">
                <Field half label="City" value={pk.city} onChange={(v) => set("addresses.pickup.city", v)} />
                <Field half label="State" value={pk.state} onChange={(v) => set("addresses.pickup.state", v)} />
                <Field half label="PIN code" value={pk.pincode} onChange={(v) => set("addresses.pickup.pincode", v)} />
              </div>
            </>
          )}
          <SaveBtn onClick={saveAddresses} busy={busy === "addr"} />
        </Section>

        {/* 3. Tax & documents */}
        <Section title="Tax & legal documents" hint="PAN and GSTIN are locked after verification — contact support to change them.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field half label="PAN" value={p.tax.pan} locked />
            <Field half label="GSTIN" value={p.tax.gstin} locked />
            <Field half label="GST status" value={p.tax.gst_status} locked />
            <Field half label="Udyam / MSME no." value={p.tax.udyam_number} locked />
          </div>
          <div className="pt-2">
            <DocRow label="PAN card" uploaded={p.tax.docs.pan_doc} onUpload={(img) => uploadDoc("pan_doc", img)} />
            <DocRow label="GST registration certificate" uploaded={p.tax.docs.gst_doc} onUpload={(img) => uploadDoc("gst_doc", img)} />
            <DocRow label="Udyam / MSME certificate" uploaded={p.tax.docs.udyam_doc} onUpload={(img) => uploadDoc("udyam_doc", img)} />
            <DocRow label="Brand authorisation (if reselling)" uploaded={p.tax.docs.brand_auth_doc} onUpload={(img) => uploadDoc("brand_auth_doc", img)} />
            <DocRow label="Other licences" uploaded={p.tax.docs.other_licenses_doc} onUpload={(img) => uploadDoc("other_licenses_doc", img)} />
          </div>
        </Section>

        {/* 4. Bank details */}
        <Section title="Bank details" hint="Where your settlements are paid. Changes may be re-verified against your cancelled cheque.">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field half label="Account holder" value={p.bank.account_holder} onChange={(v) => set("bank.account_holder", v)} />
            <Field half label="Bank name" value={p.bank.bank_name} onChange={(v) => set("bank.bank_name", v)} />
            <Field half label="Branch" value={p.bank.branch} onChange={(v) => set("bank.branch", v)} />
            <Field half label="IFSC" value={p.bank.ifsc} onChange={(v) => set("bank.ifsc", v)} />
            <Field half label="Account type" value={p.bank.account_type} onChange={(v) => set("bank.account_type", v)} placeholder="Current / Savings" />
            <Field half label="Account number"
              value={p.bank._new_account ?? ""}
              onChange={(v) => set("bank._new_account", v)}
              placeholder={p.bank.account_number_masked || "Enter account number"}
              hint={p.bank.account_number_masked ? `On file: ${p.bank.account_number_masked} — leave blank to keep` : ""} />
          </div>
          <DocRow label="Cancelled cheque / bank proof" uploaded={p.bank.cheque_uploaded} onUpload={(img) => uploadDoc("cancelled_cheque_doc", img)} />
          <SaveBtn onClick={saveBank} busy={busy === "bank"} />
        </Section>

        {/* 5. Invoicing (moved here) */}
        <Section title="Invoicing" hint="Your tax invoices carry your own number series — you are the supplier of record. The Spotters assigns only the order ID (TS…).">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Numbering mode</p>
            <div className="grid grid-cols-2 gap-2 max-w-sm">
              {[{ v: "auto", n: "Automatic series" }, { v: "manual", n: "Manual per order" }].map((m) => (
                <button key={m.v} type="button" onClick={() => set("invoicing.mode", m.v)}
                  className={`py-2.5 border font-mono text-xs uppercase tracking-widest ${p.invoicing.mode === m.v ? "bg-foreground text-background border-foreground" : "border-foreground/40 hover:border-foreground"}`}>{m.n}</button>
              ))}
            </div>
          </div>
          {p.invoicing.mode === "auto" && (
            <>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field half label="Prefix" value={p.invoicing.prefix} onChange={(v) => set("invoicing.prefix", v)} placeholder="RS/2026/" />
                <Field half label="Start at" value={p.invoicing.next_seq} onChange={(v) => set("invoicing.next_seq", v.replace(/\D/g, ""))} />
                <Field half label="Digits (padding)" value={p.invoicing.pad} onChange={(v) => set("invoicing.pad", v.replace(/\D/g, "").slice(0, 2))} />
              </div>
              <div className="border border-foreground/25 bg-secondary/40 p-3 inline-block">
                <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Next invoice: </span>
                <span className="font-mono text-lg">{invPreview || "—"}</span>
              </div>
            </>
          )}
          <div className="pt-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Authorised signature (prints on invoices)</p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[11px] text-muted-foreground">{p.has_signature ? "✓ Signature on file" : "No signature uploaded"}</span>
              <button onClick={() => sigRef.current?.click()} className="border border-foreground px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest hover:bg-foreground hover:text-background">{p.has_signature ? "Replace" : "Upload"}</button>
              <input ref={sigRef} type="file" accept="image/png,image/jpeg" onChange={uploadSig} className="hidden" />
            </div>
          </div>
          <SaveBtn onClick={saveInvoicing} busy={busy === "inv"} children="Save invoicing" />
        </Section>
      </div>
    </div>
  );
}
